import React, { useState, useRef, useCallback, useEffect } from "react";
import useLiveUpdates from "@/lib/useLiveUpdates";
import { Sparkles, Brain, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TESTIDS } from "@/constants/testIds";

export default function AICopilot({ portCallId }) {
  const [output, setOutput] = useState("");
  const [mode, setMode] = useState(null);  // explain | recommend | null
  const [streaming, setStreaming] = useState(false);
  const requestIdRef = useRef(null);

  const handleEvent = useCallback((event, payload, _topic, fullMsg) => {
    const rid = fullMsg?.request_id;
    // Only react to events tied to our active request id
    if (event === "ai_start" && rid === requestIdRef.current) {
      setOutput("");
      setStreaming(true);
    } else if (event === "ai_chunk" && rid === requestIdRef.current) {
      setOutput((prev) => prev + (payload || ""));
    } else if (event === "ai_done" && rid === requestIdRef.current) {
      setStreaming(false);
    } else if (event === "ai_error" && rid === requestIdRef.current) {
      setStreaming(false);
      setOutput((prev) => prev + "\n[error] " + (payload || "AI request failed"));
    }
  }, []);

  const { send, status } = useLiveUpdates([`port_call:${portCallId}`], handleEvent);

  const run = (which) => {
    const rid = `${which}-${Date.now()}`;
    requestIdRef.current = rid;
    setMode(which);
    setOutput("");
    setStreaming(true);
    const ok = send({ action: `ai_${which}`, port_call_id: portCallId, request_id: rid });
    if (!ok) {
      setStreaming(false);
      setOutput("Offline - please retry when reconnected.");
    }
  };

  // The backend timeout is configurable up to 120 seconds.
  useEffect(() => {
    if (!streaming) return;
    const t = setTimeout(() => {
      setStreaming(false);
      setOutput((prev) => prev || "The advisory request timed out. Deterministic planner results remain available.");
    }, 130000);
    return () => clearTimeout(t);
  }, [streaming]);

  const isDisabled = status !== "open" || streaming;

  return (
    <div className="panel p-4 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none opacity-30" style={{
        background: "linear-gradient(135deg, rgba(56,189,248,0.10), transparent 60%)",
      }} />
      <div className="relative">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-7 h-7 bg-sky-500/10 border border-sky-500/30 flex items-center justify-center">
            <Sparkles size={14} className="text-sky-400" />
          </div>
          <div className="flex-1">
            <div className="font-heading text-sm tracking-tight">AI CO-PILOT</div>
            <div className="font-mono text-[10px] text-slate-500">Advisory AI · deterministic checks remain authoritative</div>
          </div>
          <span className={`font-mono text-[9px] uppercase px-1.5 py-0.5 tracking-widest ${status === "open" ? "sev-ok" : status === "reconnecting" ? "sev-warning" : "sev-info"}`} data-testid="ai-ws-status">
            {status}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-2 mb-3">
          <Button
            data-testid={TESTIDS.btnAiExplain}
            size="sm"
            disabled={isDisabled}
            onClick={() => run("explain")}
            className="bg-[#162032] hover:bg-[#1E2B41] border border-[#1E293B] rounded-none text-slate-200 disabled:opacity-50"
          >
            <Brain size={14} className="mr-1.5" />
            Explain conflicts
          </Button>
          <Button
            data-testid={TESTIDS.btnAiRecommend}
            size="sm"
            disabled={isDisabled}
            onClick={() => run("recommend")}
            className="bg-[#162032] hover:bg-[#1E2B41] border border-[#1E293B] rounded-none text-slate-200 disabled:opacity-50"
          >
            <Sparkles size={14} className="mr-1.5" />
            Recommend
          </Button>
        </div>

        {(streaming || output) && (
          <div className="panel-2 p-3 max-h-72 overflow-y-auto text-xs text-slate-300 leading-relaxed whitespace-pre-wrap font-body" data-testid="ai-output">
            <div className={`text-[10px] uppercase tracking-widest font-mono mb-1.5 ${mode === "explain" ? "text-sky-400" : "text-amber-400"}`}>
              {mode === "explain" ? "Conflict Analysis" : "Recommendations"}
            </div>
            <div>
              {output}
              {streaming && <span className="inline-block w-1.5 h-3 ml-0.5 bg-sky-400 align-middle animate-pulse" data-testid="ai-cursor" />}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
